import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  candidateDetails,
  employeeGridMapping,
  gridList,
  registeredEmp,
  reportingManagerOfGrid,
} from '../services';
import {
  AssignGridInitialState,
  AssignGridResponseType,
  CandidateResponseType,
  ReportingManagerType,
  UserType,
} from '../utils/Types';
import { isAPISuccess } from '../utils/Util';

export const defaultAssignGridData: AssignGridResponseType = {
  responseInformation: {
    responseCode: '',
    responseMessage: '',
  },
  result: [],
  pagination: {
    currentPage: 0,
    pageSize: 0,
    totalPages: 0,
    totalItems: 0,
    fromItem: 0,
    toItem: 0,
  },
};
export const defaultGridReportingManager: ReportingManagerType = {
  reportingManager: '',
  reportingManagerRole: '',
  reportingManagerVillageName: '',
  reportingManagerStateName: '',
};
export const blankUserDetails: UserType = {
  candidateId: 0,
  candidateid: 0,
  employeeId: '',
  candidateUniqueId: '',
  candidateuniqueid: '',
  employeeUniqueId: '',
  firstname: '',
  lastname: '',
  gender: '',
  address: '',
  location: 0,
  locationname: '',
  cityname: '',
  city: 0,
  state: 0,
  zone: 0,
  statename: '',
  pincode: 0,
  maritalStatus: '',
  photo: '',
  mobileNumber: 0,
  isMobileVerified: false,
  emailId: '',
  isEmailVerified: false,
  roleId: 0,
  roleName: '',
  userActivationStatus: 0,
  userActivationStatusName: '',
  kycStatus: 0,
  kycStatusName: '',
  trainingStatus: false,
  testStatus: false,
  approvedBy: 0,
  approvedByName: '',
  approvedOn: '',
  isDeleted: false,
  createdOn: '',
  lastModifiedOn: '',
  reportingManagerId: 0,
  reportingManagerName: '',
  reportingManagerNumber: 0,
  reportingmanagerid: '',
  reportingmanagername: '',
  reportingmanagernumber: '',
  mobilenumber: 0,
  approvedbyname: null,
  maritalstatus: '',
  approvedon: null,
  createdon: '',
  emailid: '',
  statusname: '',
  approvedby: null,
  ismobileverified: false,
  isdeleted: false,
  teststatus: false,
  trainingstatus: false,
  isemailverified: false,
  useractivationstatus: 0,
  lastmodifiedon: '',
  roleid: 0,
  employeeuniqueid: null,
  dob: null,
  kycstatus: 0,
  reportingZones: [],
  reportingStates: [],
  assignedgrids: '',
  districtName: '',
  fullName: '',
  fullname: '',
  district: '',
  cityName: null,
  stateName: null,
  zoneName: null,
};
export const initialState: AssignGridInitialState = {
  assignGridData: defaultAssignGridData,
  selectedCandidate: {
    responseInformation: {
      responseCode: '',
      responseMessage: '',
    },
    result: {
      user: blankUserDetails,
      grids: [],
      notes: [],
      campaignDetails: [],
      documents: [],
    },
  },
  gridReportingManager: defaultGridReportingManager,
};
/**
 * Async API call to find registered employee list (Assign grid data)
 */
export const getRegisteredEmployees = createAsyncThunk(
  'assignGrid/fetchEmployees',
  async (params: any) => {
    const res = await registeredEmp(params);
    return res.data;
  }
);
/**
 * Async API call to find candidate details
 */
export const getCandidateDetails = createAsyncThunk(
  'assignGrid/fetchCandidate',
  async (candidateId: number | string) => {
    const res = await candidateDetails({ candidateId });
    return res.data;
  }
);
/**
 * Async API call to find grids on the basis of pincode
 */
export const getGridList = createAsyncThunk(
  'assignGrid/fetchGridList',
  async (data: any) => {
    const res = await gridList({
      villageId:
        data && data.vid && data.vid.length ? data.vid.map(Number) : null,
      pincode: data && data.vid && data.vid.length ? null : [data?.pincode],
      candidateId: data?.candidateId,
    });
    return res.data;
  }
);
/**
 * Async API call to assign selected grids to employee
 */
export const assignGridToEmployee = createAsyncThunk(
  'assignGrid/assignGrid',
  async (data: any) => {
    const res = await employeeGridMapping(data);
    return res.data;
  }
);
/**
 * Async API call to reporting manager details of grid/pincode
 */
export const getReportingManagerOfGrid = createAsyncThunk(
  'assignGrid/reportingManagerOfGrid',
  async (pincode: number) => {
    const res = await reportingManagerOfGrid({ pincode });
    return res.data;
  }
);

export const assignGridSlice = createSlice({
  name: 'assignGrid',
  initialState,
  reducers: {
    updateSelectedCandidate: (state, action) => {
      state.selectedCandidate.result.user = action.payload;
    },
    clearReportingManagerData: (state) => {
      state.gridReportingManager = defaultGridReportingManager;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(
        getRegisteredEmployees.fulfilled,
        (state, action: PayloadAction<AssignGridResponseType>) => {
          state.assignGridData.pagination = action.payload.pagination;
          state.assignGridData.result = action.payload.result;
          state.assignGridData.responseInformation =
            action.payload.responseInformation;
        }
      )
      .addCase(
        getCandidateDetails.fulfilled,
        (state, action: PayloadAction<CandidateResponseType>) => {
          state.selectedCandidate.result = action.payload.result;
          state.selectedCandidate.responseInformation =
            action.payload.responseInformation;
        }
      )
      .addCase(getGridList.fulfilled, () => {})
      .addCase(getReportingManagerOfGrid.fulfilled, (state, action) => {
        if (isAPISuccess(action.payload)) {
          state.gridReportingManager = action.payload.result;
        } else {
          state.gridReportingManager = defaultGridReportingManager;
        }
      });
  },
});

export const assignGridSelector = (state: {
  assignGrid: AssignGridInitialState;
}) => state.assignGrid;

export const candidateSelector = (state: {
  assignGrid: AssignGridInitialState;
}) => state.assignGrid?.selectedCandidate.result;

export const { updateSelectedCandidate, clearReportingManagerData } =
  assignGridSlice.actions;
export default assignGridSlice.reducer;
